import React, { useState, useEffect } from 'react';
import { Achievement } from '../../types/game';
import { createConfetti } from '../../utils/confetti';
import { getAvatarUrl } from '../../utils/avatar';
import { useTheme } from '../../contexts/ThemeContext';

interface AchievementsScreenProps {
  achievements: Achievement[];
}

const AchievementsScreen: React.FC<AchievementsScreenProps> = ({ achievements }) => {
  const { theme } = useTheme();
  const [currentAchievementIndex, setCurrentAchievementIndex] = useState(0);
  const [showAchievement, setShowAchievement] = useState(false);

  useEffect(() => {
    if (achievements.length === 0) return;

    // Show first achievement after a short delay and trigger confetti
    const showTimer = setTimeout(() => {
      setShowAchievement(true);
      createConfetti();
    }, 300);

    return () => clearTimeout(showTimer);
  }, [achievements]);

  useEffect(() => {
    if (achievements.length === 0) return;
    if (currentAchievementIndex >= achievements.length - 1) return;

    // Cycle through achievements every 7 seconds
    const cycleTimer = setTimeout(() => {
      setShowAchievement(false);
      setTimeout(() => {
        setCurrentAchievementIndex(prev => prev + 1);
        setShowAchievement(true);
        createConfetti(); // Trigger confetti for each new achievement
      }, 300);
    }, 7000);

    return () => clearTimeout(cycleTimer);
  }, [currentAchievementIndex, achievements]);

  if (achievements.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-8">
            <span className="text-8xl">⏳</span>
          </div>
          <h1
            className="text-4xl md:text-6xl font-black mb-4 drop-shadow-lg"
            style={{ color: theme.colors.textPrimary }}
          >
            Great work!
          </h1>
          <p
            className="text-xl md:text-2xl drop-shadow-md"
            style={{ color: theme.colors.textSecondary }}
          >
            Get ready for the next round...
          </p>
        </div>
      </div>
    );
  }

  const currentAchievement = achievements[currentAchievementIndex];

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center">
        <div className={`mb-8 transition-all duration-500 ${showAchievement ? 'animate-bounce scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
          <span className="text-9xl">🏆</span>
        </div>

        <div className={`transition-all duration-500 ${showAchievement ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div
            className="backdrop-blur-md rounded-2xl p-8 border-4 shadow-2xl"
            style={{
              backgroundColor: theme.colors.cardBackground,
              borderColor: theme.colors.accent
            }}
          >
            <h1
              className="text-3xl md:text-5xl font-black mb-3 drop-shadow-lg"
              style={{ color: theme.colors.accent }}
            >
              {currentAchievement.title}
            </h1>
            <p
              className="text-xl md:text-2xl drop-shadow-md mb-6"
              style={{ color: theme.colors.textSecondary }}
            >
              {currentAchievement.description}
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              <span
                className="text-2xl md:text-3xl font-bold"
                style={{ color: theme.colors.correct }}
              >
                +{currentAchievement.bonusPoints}
              </span>
              <span
                className="text-lg md:text-xl"
                style={{ color: theme.colors.textSecondary }}
              >
                points
              </span>
            </div>

            {/* Player highlight section with avatar */}
            <div
              className="flex flex-col items-center gap-4 rounded-xl p-6 border-2"
              style={{
                backgroundColor: `${theme.colors.accent}33`, // 20% opacity
                borderColor: `${theme.colors.accent}80` // 50% opacity
              }}
            >
              <img
                src={getAvatarUrl(currentAchievement.playerName, 128)}
                alt={`${currentAchievement.playerName}'s avatar`}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full shadow-lg animate-pulse"
                style={{ borderWidth: '4px', borderColor: theme.colors.accent }}
              />
              <div className="text-center">
                <p
                  className="text-sm md:text-base font-semibold mb-1"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Achieved by
                </p>
                <p
                  className="text-2xl md:text-3xl font-black drop-shadow-lg"
                  style={{ color: theme.colors.textPrimary }}
                >
                  {currentAchievement.playerName}
                </p>
              </div>
            </div>
          </div>

          {achievements.length > 1 && (
            <div className="mt-6 flex gap-2 justify-center">
              {achievements.map((_, idx) => (
                <div
                  key={idx}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{
                    backgroundColor: idx === currentAchievementIndex ? theme.colors.accent : 'rgba(255, 255, 255, 0.3)',
                    width: idx === currentAchievementIndex ? '24px' : '8px'
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AchievementsScreen;
